/**
 * Module Contrats — conteneur principal.
 *
 * Gère les contrats du foyer (énergie, eau, internet, assurance…) :
 * fournisseur, prix d'abonnement, fréquence de paiement, items du contrat,
 * lien vers l'espace client, documents / factures et interprétation IA des
 * factures. Les consommations d'énergie extraites alimentent le dashboard
 * écologie (widget « Consommations d'énergie »).
 *
 * Route : /contrats (cf. App.js). Même trame que le module Santé.
 */
import React, { useEffect, useState } from 'react'
import {
  Box,
  Button,
  Chip,
  Container,
  Grid,
  IconButton,
  Stack,
  Typography,
} from '@mui/material'
import ArrowBackIcon from '@mui/icons-material/ArrowBack'
import AddRoundedIcon from '@mui/icons-material/AddRounded'
import DescriptionRoundedIcon from '@mui/icons-material/DescriptionRounded'
import BoltRoundedIcon from '@mui/icons-material/BoltRounded'
import OpenInNewRoundedIcon from '@mui/icons-material/OpenInNewRounded'
import { useNavigate } from 'react-router-dom'

import { contratsApi } from '../api/contratsApi'
import {
  CONTRACTS_ACCENT,
  cardSx,
  contractTypeMeta,
  fmtEuro,
  PAGE_BG,
  PAYMENT_FREQUENCIES,
} from './shared'
import ContractDialog from './ContractDialog'
import ContractDetail from './ContractDetail'

const frequencyLabel = (value) =>
  (PAYMENT_FREQUENCIES.find((f) => f.value === value) || {}).label || value

export default function ContratsModule() {
  const navigate = useNavigate()
  const [contracts, setContracts] = useState([])
  const [loading, setLoading] = useState(true)
  const [dialogOpen, setDialogOpen] = useState(false)
  const [editing, setEditing] = useState(null)
  const [selectedId, setSelectedId] = useState(null)

  const loadContracts = async () => {
    setLoading(true)
    const list = await contratsApi.contracts.list()
    setContracts(list)
    setLoading(false)
  }

  useEffect(() => {
    loadContracts()
    window.scrollTo(0, 0)
  }, [])

  const totalMonthly = contracts
    .filter((c) => c.is_active)
    .reduce((sum, c) => sum + Number(c.monthly_cost || 0), 0)

  const handleSaved = async () => {
    setDialogOpen(false)
    setEditing(null)
    await loadContracts()
  }

  const selected = contracts.find((c) => c.id === selectedId)

  return (
    <Box sx={{ bgcolor: PAGE_BG, minHeight: '100vh', pb: 6 }}>
      <Container maxWidth="lg" sx={{ pt: 3 }}>
        <Stack direction="row" spacing={1.5} alignItems="center" sx={{ mb: 2 }}>
          <IconButton
            onClick={() => (selected ? setSelectedId(null) : navigate('/'))}
            sx={{ color: '#fff' }}
          >
            <ArrowBackIcon />
          </IconButton>
          <BoltRoundedIcon sx={{ color: CONTRACTS_ACCENT }} />
          <Typography sx={{ color: '#fff', fontWeight: 800, fontSize: 22 }}>
            Mes Contrats
          </Typography>
          <Box sx={{ flexGrow: 1 }} />
          {!selected && (
            <Button
              variant="contained"
              startIcon={<AddRoundedIcon />}
              onClick={() => {
                setEditing(null)
                setDialogOpen(true)
              }}
              sx={{
                bgcolor: CONTRACTS_ACCENT,
                color: '#00001d',
                fontWeight: 700,
                '&:hover': { bgcolor: '#ffc76e' },
              }}
            >
              Nouveau contrat
            </Button>
          )}
        </Stack>

        {selected ? (
          <ContractDetail
            contract={selected}
            onEdit={() => {
              setEditing(selected)
              setDialogOpen(true)
            }}
            onChanged={loadContracts}
            onDeleted={() => {
              setSelectedId(null)
              loadContracts()
            }}
          />
        ) : (
          <>
            <Typography sx={{ color: 'rgba(255,255,255,0.6)', mb: 2 }}>
              {loading
                ? 'Chargement…'
                : `${contracts.length} contrat${contracts.length > 1 ? 's' : ''} — ` +
                  `budget abonnements ≈ ${fmtEuro(totalMonthly)} / mois`}
            </Typography>

            {!loading && contracts.length === 0 && (
              <Box sx={{ ...cardSx, textAlign: 'center', py: 6 }}>
                <Typography sx={{ color: '#fff', fontWeight: 700, mb: 1 }}>
                  Aucun contrat pour l'instant
                </Typography>
                <Typography sx={{ color: 'rgba(255,255,255,0.6)' }}>
                  Ajoutez vos contrats d'énergie, d'eau ou d'internet, attachez vos
                  factures et laissez l'IA en extraire consommations, coûts et
                  anomalies pour votre dashboard écologie.
                </Typography>
              </Box>
            )}

            <Grid container spacing={2}>
              {contracts.map((contract) => {
                const meta = contractTypeMeta(contract.contract_type)
                return (
                  <Grid item xs={12} sm={6} md={4} key={contract.id}>
                    <Box
                      onClick={() => setSelectedId(contract.id)}
                      sx={{
                        ...cardSx,
                        cursor: 'pointer',
                        height: '100%',
                        transition: 'border-color .15s',
                        '&:hover': { borderColor: meta.color },
                        opacity: contract.is_active ? 1 : 0.55,
                      }}
                    >
                      <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 1 }}>
                        <Chip
                          size="small"
                          label={meta.label}
                          sx={{ bgcolor: `${meta.color}22`, color: meta.color, fontWeight: 700 }}
                        />
                        {!contract.is_active && (
                          <Chip
                            size="small"
                            label="Résilié"
                            sx={{ bgcolor: 'rgba(255,255,255,0.1)', color: '#fff' }}
                          />
                        )}
                      </Stack>
                      <Typography sx={{ color: '#fff', fontWeight: 700, fontSize: 17 }}>
                        {contract.name}
                      </Typography>
                      <Typography sx={{ color: 'rgba(255,255,255,0.6)', fontSize: 14 }}>
                        {contract.provider || 'Fournisseur non renseigné'}
                      </Typography>
                      <Typography sx={{ color: meta.color, fontWeight: 700, mt: 1 }}>
                        {fmtEuro(contract.subscription_price)}{' '}
                        <Typography component="span" sx={{ color: 'rgba(255,255,255,0.5)', fontSize: 13 }}>
                          / {frequencyLabel(contract.payment_frequency).toLowerCase()}
                        </Typography>
                      </Typography>
                      <Stack direction="row" spacing={2} sx={{ mt: 1.5 }} alignItems="center">
                        <Stack direction="row" spacing={0.5} alignItems="center">
                          <DescriptionRoundedIcon sx={{ fontSize: 16, color: 'rgba(255,255,255,0.5)' }} />
                          <Typography sx={{ color: 'rgba(255,255,255,0.5)', fontSize: 13 }}>
                            {contract.documents_count || 0} document
                            {(contract.documents_count || 0) > 1 ? 's' : ''}
                          </Typography>
                        </Stack>
                        {contract.customer_portal_url && (
                          <Stack
                            direction="row"
                            spacing={0.5}
                            alignItems="center"
                            component="a"
                            href={contract.customer_portal_url}
                            target="_blank"
                            rel="noopener noreferrer"
                            onClick={(e) => e.stopPropagation()}
                            sx={{ textDecoration: 'none' }}
                          >
                            <OpenInNewRoundedIcon sx={{ fontSize: 16, color: CONTRACTS_ACCENT }} />
                            <Typography sx={{ color: CONTRACTS_ACCENT, fontSize: 13 }}>
                              Espace client
                            </Typography>
                          </Stack>
                        )}
                      </Stack>
                    </Box>
                  </Grid>
                )
              })}
            </Grid>
          </>
        )}

        <ContractDialog
          open={dialogOpen}
          contract={editing}
          onClose={() => {
            setDialogOpen(false)
            setEditing(null)
          }}
          onSaved={handleSaved}
        />
      </Container>
    </Box>
  )
}
